// The ONE lift-device decision, so decoders and lifter cannot disagree about which GPU owns a buffer.

import { wantsProfiling } from "./perfFlags";
import { installWebGpuLimitPatch } from "../../lib/webgpuLimits.js";
import { requestSharedGpuDevice } from "../../lib/sharedGpuDevice.js";
import { ogsGpuFlags, type OgsGpuFlags } from "./ogsGpuFlags";
import { planLiftDevice, liftModeFor } from "./liftDeviceRule.js";

// WebGPU's DEFAULT maxStorageBuffersPerShaderStage, which every adapter grants — the lift's
// heaviest pass binds exactly 8, so no device is refused the GPU lift over a limit.
const REQUIRED_STORAGE_BUFFERS = 8;
// ASK FOR WHAT WE USE. This was 10 while one pass wanted more than the default;
// nothing does now (every shader binds 8 or fewer, including the pass 2 that is
// derived at load — liftStorageBuffers.test.mjs pins them). Asking higher is not
// free: the spec lets an implementation hand back a slower device for a raised
// limit, and it would be asking on behalf of a pass that no longer exists.
const REQUESTED_STORAGE_BUFFERS = REQUIRED_STORAGE_BUFFERS;

/** timestamp-query under ?perf=1 only, and only when the adapter has it — else create rejects. */
function profilingFeatures(adapter: GPUAdapter): GPUFeatureName[] {
  return wantsProfiling() && adapter.features?.has?.("timestamp-query")
    ? ["timestamp-query"]
    : [];
}

let devicePromise: Promise<GPUDevice | null> | null = null;

let sharedSetupDone = false;
let sharedSetupOk = false;

/** GIVE ORT THE PAGE'S DEVICE. MUST run before the first session create; never throws.
 *
 *  It used to only nudge ORT toward a device the lift could share — request the
 *  discrete adapter, patch `requestDevice` — and then read back whatever ORT
 *  built, because ORT-Web's `env.webgpu.adapter` is read-only. 1.26 lets a
 *  `GPUDevice` be SET (`env.webgpu.device`, "before the first WebGPU inference
 *  session is created"), so the direction is now the other way round: the page
 *  makes ONE device (`sharedGpuDevice.js`) and ORT is handed it, which is what
 *  puts the decoders, the lift and the RENDERER on the same device. */
export async function setupSharedOrtAdapter(ort: any): Promise<boolean> {
  if (sharedSetupDone) return sharedSetupOk;
  sharedSetupDone = true;
  try {
    const webgpu = (navigator as unknown as { gpu?: GPU }).gpu;
    if (!webgpu || !ort?.env?.webgpu) return false;
    const shared = await requestSharedGpuDevice();
    if (shared) {
      const granted = shared.limits?.maxStorageBuffersPerShaderStage ?? 0;
      if (granted < REQUIRED_STORAGE_BUFFERS) return false;
      // Setting it is what makes the decoders run on the renderer's device. A
      // throw here means a session already exists — the device is then ORT's own
      // and the lift falls back to reading it, exactly as before.
      try {
        ort.env.webgpu.device = shared;
      } catch (e) {
        console.warn("[texavatars] ORT would not take the shared device (a session already exists?) "
          + "— falling back to the device ORT builds", e);
      }
      sharedSetupOk = true;
      return true;
    }
    // NO SHARED DEVICE — `?gpudevice=0`, no WebGPU at all, or a creation that
    // failed. The old route: nudge ORT toward the discrete adapter and adopt
    // whatever device it builds. The page is then back on two devices, which is
    // what `sharedGpuDevice.js` says out loud when it happens.
    // high-performance picks the discrete GPU; the default runs all inference on integrated.
    const adapter = await webgpu.requestAdapter({ powerPreference: "high-performance" });
    if (!adapter) return false;
    const adapterMaximum = adapter.limits?.maxStorageBuffersPerShaderStage ?? 8;
    if (Math.min(REQUESTED_STORAGE_BUFFERS, adapterMaximum) < REQUIRED_STORAGE_BUFFERS) return false;
    // Re-assert of the boot patch, for a caller that arrives without main.jsx's import.
    installWebGpuLimitPatch();
    try { ort.env.webgpu.powerPreference = "high-performance"; } catch { /* read-only on some builds */ }
    sharedSetupOk = true;
    return true;
  } catch (error) {
    console.warn("[texavatars] shared ORT adapter setup failed — lift uses its own device/CPU", error);
    return false;
  }
}

/** The device ORT is actually running on, if it satisfies the lift's storage-buffer
 *  need. Normally the page's shared device, which is also the renderer's — read
 *  back from ORT rather than assumed, so a path where ORT built its own anyway
 *  still reports the truth. */
export function getSharedOrtDevice(ort: any): GPUDevice | null {
  const ortDevice = ort?.env?.webgpu?.device as GPUDevice | undefined;
  if (!ortDevice) return null;
  const granted = ortDevice.limits?.maxStorageBuffersPerShaderStage ?? 0;
  return granted >= REQUIRED_STORAGE_BUFFERS ? ortDevice : null;
}

/** THERE IS ONE DEVICE OR THERE IS NONE.
 *
 *  This used to read `"shared" | "own" | "cpu"`, from the era when ORT-Web's
 *  `env.webgpu.adapter` was READ-ONLY: the page could not give ORT a device, only
 *  nudge it toward an adapter and read back whatever it built — so two devices on
 *  one page was a real outcome and `"own"` named it (the lift on its own device,
 *  every decoder output round-tripping through the CPU). 1.26 lets the device be
 *  SET, `sharedGpuDevice.js` makes one at boot for three AND ORT, and
 *  `getOgsComputeDevice` asks for that one FIRST — so `"own"` returned the very
 *  same object under a different name. A lift device that is not the page's is
 *  now a REFUSAL that says so (`sharedDeviceSatisfies`), never a third mode. */
export type OgsLiftMode = "gpu" | "cpu";

export interface OgsLiftPrepared {
  /** Emit gpu-buffer outputs + build lifters/pluckers on ORT's device. */
  readonly shared: boolean;
  readonly flags: OgsGpuFlags;
}

export interface OgsLiftDevice {
  readonly device: GPUDevice | null;
  readonly mode: OgsLiftMode;
  /** The sessions' gpu-buffer hints must be DEMOTED to CPU downloads. This is
   *  about the IO only — the lift still runs on `device`. Keeping the two apart
   *  is the point of this file: "which device" and "do the decoder outputs stay
   *  on it" are independent, and one field answering both is what made `?gpubuf=0`
   *  silently also turn off the GPU plücker. */
  readonly demoted: boolean;
}

/** Step 1: ask ORT for a shareable device. Call BEFORE creating any session. */
export async function prepareOgsLiftDevice(
  ort: any,
  providers: string[],
  flags: OgsGpuFlags = ogsGpuFlags(),
): Promise<OgsLiftPrepared> {
  const wantShared = flags.gpuBufLift && providers[0] === "webgpu";
  const shared = wantShared ? await setupSharedOrtAdapter(ort) : false;
  return { shared, flags };
}

/** Step 3: the device to build lifters on, AFTER the sessions exist. GPU → CPU. */
export async function resolveOgsLiftDevice(
  ort: any,
  prepared: OgsLiftPrepared,
): Promise<OgsLiftDevice> {
  const { flags } = prepared;
  const ortDevice = prepared.shared ? getSharedOrtDevice(ort) : null;
  // The RULE is next door and pure, so it can be driven by a test that cannot
  // import this file (extensionless imports need a bundler) — and so nobody has
  // to restate it to test it.
  const plan = planLiftDevice({
    ioRequested: prepared.shared,
    ortDeviceUsable: !!ortDevice,
    gpuLift: flags.gpuLift,
  });

  if (plan.demoted) {
    console.warn(
      "[texavatars] ORT's actual WebGPU device fails the lift limits (adapter mismatch?) — demoting the IO: outputs read back to CPU. The lift still runs on the page device.",
    );
  }

  const device = plan.source === "ort"
    ? ortDevice
    : plan.source === "page" ? await getTexavatarsGpuDevice() : null;

  if (!device) {
    console.warn(
      `[ogs] lift: CPU (GaussianLifter) — no WebGPU adapter (or ?gpulift=0 / a device granting < ${REQUIRED_STORAGE_BUFFERS} storage buffers). The branch reports this as a downgrade.`,
    );
  }
  return { device, mode: liftModeFor(device), demoted: plan.demoted };
}

export function getTexavatarsGpuDevice(): Promise<GPUDevice | null> {
  return getOgsComputeDevice(REQUIRED_STORAGE_BUFFERS, "GpuLifter");
}

/** One cached device, refused per CALLER: lift_pass1 binds 8, the eye's polar lift binds 5. */
export function getOgsComputeDevice(
  minStorageBuffers: number,
  caller = "lift",
): Promise<GPUDevice | null> {
  if (!devicePromise) {
    devicePromise = (async () => {
      try {
        // THE PAGE'S DEVICE FIRST. A compute pass that lifts into buffers the
        // renderer draws has to be on the renderer's device; asking the browser
        // for a fresh one here is what used to guarantee it was not.
        const shared = await requestSharedGpuDevice();
        if (shared) return shared;
        const webgpu = (navigator as unknown as { gpu?: GPU }).gpu;
        if (!webgpu) return null;
        const adapter = await webgpu.requestAdapter({ powerPreference: "high-performance" });
        if (!adapter) return null;
        // Clamp the request to the adapter maximum so requestDevice never rejects.
        const adapterMaximum = adapter.limits?.maxStorageBuffersPerShaderStage ?? 8;
        return await adapter.requestDevice({
          requiredLimits: {
            maxStorageBuffersPerShaderStage: Math.min(REQUESTED_STORAGE_BUFFERS, adapterMaximum),
          },
          requiredFeatures: profilingFeatures(adapter),
        });
      } catch (error) {
        console.warn("[texavatars] WebGPU compute device unavailable", error);
        return null;
      }
    })();
  }
  return devicePromise.then((device) => {
    if (!device) return null;
    if (grants(device, minStorageBuffers)) return device;
    console.warn(
      `[texavatars] ${caller} needs maxStorageBuffersPerShaderStage>=${minStorageBuffers}, this device grants ${device.limits?.maxStorageBuffersPerShaderStage}`,
    );
    return null;
  });
}

function grants(device: GPUDevice, minStorageBuffers: number): boolean {
  return (device.limits?.maxStorageBuffersPerShaderStage ?? 8) >= minStorageBuffers;
}
